import React, { useState, useEffect, useRef } from 'react';
import Prism from 'prismjs';
import 'prismjs/components/prism-json';
import TryItPanel from './TryItPanel';
import { schemaToExampleJson } from '../utils/parseSpec';

const METHOD_STYLES = {
  get:     { color: 'var(--method-get)',     bg: 'var(--method-get-bg)' },
  post:    { color: 'var(--method-post)',    bg: 'var(--method-post-bg)' },
  put:     { color: 'var(--method-put)',     bg: 'var(--method-put-bg)' },
  patch:   { color: 'var(--method-patch)',   bg: 'var(--method-patch-bg)' },
  delete:  { color: 'var(--method-delete)',  bg: 'var(--method-delete-bg)' },
  head:    { color: 'var(--method-head)',    bg: 'var(--method-head-bg)' },
  options: { color: 'var(--method-options)', bg: 'var(--method-options-bg)' },
};

function statusCodeColor(code) {
  const c = String(code);
  if (c.startsWith('2')) return 'var(--method-get)';
  if (c.startsWith('3')) return 'var(--method-post)';
  if (c.startsWith('4')) return 'var(--method-put)';
  if (c.startsWith('5')) return 'var(--method-delete)';
  return 'var(--muted-foreground)';
}

const s = {
  card: {
    border: '1px solid var(--border)', borderRadius: 'var(--radius)',
    overflow: 'hidden', marginBottom: '8px',
    background: 'var(--card)',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  },
  cardHover: { borderColor: 'var(--ring)' },
  header: {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '12px 16px', cursor: 'pointer', userSelect: 'none',
    background: 'var(--card)',
  },
  badge: {
    fontFamily: '"Geist Mono", monospace', fontSize: '10px', fontWeight: 600,
    padding: '2px 8px', borderRadius: 'calc(var(--radius) * 0.6)',
    textTransform: 'uppercase', letterSpacing: '0.06em',
    minWidth: '56px', textAlign: 'center', flexShrink: 0,
  },
  path: {
    fontFamily: '"Geist Mono", monospace', fontSize: '12.5px',
    color: 'var(--foreground)', flex: 1, wordBreak: 'break-all',
  },
  summary: {
    fontSize: '12px', color: 'var(--muted-foreground)',
    flexShrink: 0, maxWidth: '240px',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  chevron: {
    flexShrink: 0, color: 'var(--muted-foreground)',
    transition: 'transform 0.2s ease',
  },
  body: {
    padding: '18px 16px', borderTop: '1px solid var(--border)',
    background: 'var(--background)',
    display: 'flex', flexDirection: 'column', gap: '16px',
  },
  tabBar: {
    display: 'flex', borderBottom: '1px solid var(--border)', marginBottom: '4px',
  },
  tab: {
    fontFamily: '"Geist Mono", monospace', fontSize: '11px',
    padding: '6px 12px', border: 'none', background: 'none',
    color: 'var(--muted-foreground)', cursor: 'pointer',
    borderBottom: '2px solid transparent', marginBottom: '-1px',
    transition: 'color 0.15s',
  },
  tabActive: { color: 'var(--foreground)', borderBottomColor: 'var(--foreground)' },
  sectionLabel: {
    fontFamily: '"Geist Mono", monospace', fontSize: '10px',
    letterSpacing: '0.08em', textTransform: 'uppercase',
    color: 'var(--muted-foreground)', marginBottom: '8px',
  },
  description: {
    fontSize: '13px', color: 'var(--muted-foreground)', lineHeight: 1.7,
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  th: {
    textAlign: 'left', padding: '6px 10px',
    borderBottom: '1px solid var(--border)',
    fontFamily: '"Geist Mono", monospace', fontSize: '10px',
    fontWeight: 500, letterSpacing: '0.08em', textTransform: 'uppercase',
    color: 'var(--muted-foreground)',
  },
  td: {
    padding: '8px 10px', borderBottom: '1px solid var(--border)',
    color: 'var(--muted-foreground)', verticalAlign: 'top', lineHeight: 1.5,
  },
  tdName: { fontFamily: '"Geist Mono", monospace', color: 'var(--foreground)' },
  required: { color: 'var(--destructive)', fontSize: '10px', marginLeft: '3px' },
  tagPill: {
    fontFamily: '"Geist Mono", monospace', fontSize: '11px',
    padding: '2px 8px', borderRadius: 'calc(var(--radius) * 0.5)',
    border: '1px solid var(--border)',
    background: 'var(--secondary)', color: 'var(--muted-foreground)',
  },
  responseRow: {
    display: 'flex', alignItems: 'baseline', gap: '10px',
    padding: '7px 0', borderBottom: '1px solid var(--border)',
  },
  statusCode: {
    fontFamily: '"Geist Mono", monospace', fontSize: '12px',
    fontWeight: 600, minWidth: '36px', flexShrink: 0,
  },
  respDesc: { fontSize: '12px', color: 'var(--muted-foreground)' },
};

export default function EndpointCard({ path, method, operation, spec }) {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('docs');
  const [hovered, setHovered] = useState(false);
  const bodyRef = useRef(null);

  const ms = METHOD_STYLES[method] || METHOD_STYLES.options;
  const params = operation.parameters || [];
  const responses = operation.responses || {};

  let requestBodyExample = null;
  if (operation.requestBody) {
    const content = operation.requestBody.content || {};
    const jc = content['application/json'] || Object.values(content)[0];
    if (jc?.schema) requestBodyExample = schemaToExampleJson(jc.schema, spec.components);
  }

  useEffect(() => {
    if (open && bodyRef.current) Prism.highlightAllUnder(bodyRef.current);
  }, [open, activeTab]);

  const getResponseSchema = (resp) => {
    const content = resp?.content || {};
    const jc = content['application/json'] || Object.values(content)[0];
    return jc?.schema || null;
  };

  return (
    <div
      style={{ ...s.card, ...(hovered && !open ? { borderColor: 'var(--ring)', boxShadow: '0 0 0 1px var(--ring)' } : {}) }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Header */}
      <div style={s.header} onClick={() => setOpen((o) => !o)}>
        <span style={{ ...s.badge, color: ms.color, background: ms.bg }}>
          {method.toUpperCase()}
        </span>
        <span style={s.path}>{path}</span>
        {operation.summary && <span style={s.summary}>{operation.summary}</span>}
        <svg
          style={{ ...s.chevron, transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
          width="14" height="14" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>

      {/* Body */}
      {open && (
        <div style={s.body} ref={bodyRef}>
          {/* Tabs */}
          <div style={s.tabBar}>
            {['docs', 'try-it'].map((tab) => (
              <button
                key={tab}
                style={{ ...s.tab, ...(activeTab === tab ? s.tabActive : {}) }}
                onClick={() => setActiveTab(tab)}
              >
                {tab === 'try-it' ? 'Try it ▶' : 'Docs'}
              </button>
            ))}
          </div>

          {activeTab === 'docs' && (
            <>
              {operation.description && (
                <p style={s.description}>{operation.description}</p>
              )}

              {/* Tags */}
              {operation.tags?.length > 0 && (
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {operation.tags.map((tag) => (
                    <span key={tag} style={s.tagPill}>{tag}</span>
                  ))}
                </div>
              )}

              {/* Parameters */}
              {params.length > 0 && (
                <div>
                  <div style={s.sectionLabel}>Parameters</div>
                  <table style={s.table}>
                    <thead>
                      <tr>
                        {['Name', 'In', 'Type', 'Required', 'Description'].map((h) => (
                          <th key={h} style={s.th}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {params.map((p, i) => {
                        const schema = p.schema || {};
                        return (
                          <tr key={i}>
                            <td style={{ ...s.td, ...s.tdName }}>
                              {p.name}
                              {p.required && <span style={s.required}>*</span>}
                            </td>
                            <td style={s.td}>{p.in}</td>
                            <td style={{ ...s.td, fontFamily: '"Geist Mono", monospace', fontSize: '11px', color: 'var(--method-post)' }}>
                              {schema.type || p.type || '—'}
                              {schema.enum ? ` (${schema.enum.join(' | ')})` : ''}
                            </td>
                            <td style={s.td}>
                              {p.required
                                ? <span style={{ color: 'var(--destructive)' }}>yes</span>
                                : <span style={{ color: 'var(--muted-foreground)' }}>no</span>}
                            </td>
                            <td style={s.td}>{p.description || '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Request body */}
              {requestBodyExample !== null && (
                <div>
                  <div style={s.sectionLabel}>
                    Request Body
                    {operation.requestBody?.required && (
                      <span style={{ ...s.required, marginLeft: '6px' }}>required</span>
                    )}
                  </div>
                  <pre className="language-json">
                    <code className="language-json">
                      {JSON.stringify(requestBodyExample, null, 2)}
                    </code>
                  </pre>
                </div>
              )}

              {/* Responses */}
              {Object.keys(responses).length > 0 && (
                <div>
                  <div style={s.sectionLabel}>Responses</div>
                  {Object.entries(responses).map(([code, resp]) => {
                    const schema = getResponseSchema(resp);
                    const example = schema ? schemaToExampleJson(schema, spec.components) : null;
                    return (
                      <div key={code} style={{ marginBottom: '8px' }}>
                        <div style={s.responseRow}>
                          <span style={{ ...s.statusCode, color: statusCodeColor(code) }}>{code}</span>
                          <span style={s.respDesc}>{resp.description || ''}</span>
                        </div>
                        {example !== null && (
                          <pre className="language-json" style={{ marginTop: '6px' }}>
                            <code className="language-json">
                              {JSON.stringify(example, null, 2)}
                            </code>
                          </pre>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {activeTab === 'try-it' && (
            <TryItPanel
              path={path}
              method={method}
              operation={operation}
              servers={spec.servers}
              spec={spec}
            />
          )}
        </div>
      )}
    </div>
  );
}
